import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    EquipmentDto,
    EquipmentService,
    FlyFreelyLoggingService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    PersonsOrganisationDto,
    ShareResourceCommand,
    UnshareResourceCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

@Component({
    selector: 'shared-equipment',
    styleUrls: ['./common.scss'],
    template: `<h5 [ngClass]="{ readonly: working }">Shared Equipment</h5>
        <div class="input-group">
            <ng-select
                [items]="
                    organisationEquipmentList$
                        | async
                        | sortBy: 'name'
                        | filterOutById: relationship.sharedEquipmentList
                "
                bindValue="id"
                appendTo="body"
                [formControl]="addEquipment"
                placeholder="Select equipment to share with {{
                    relationship.childOrganisation.name
                }}"
                [readonly]="working || isReadOnlyAdmin"
                [clearable]="false"
                ><ng-template ng-option-tmp let-item="item" let-index="index">
                    <equipment-list-item [item]="item"></equipment-list-item>
                </ng-template>
                <ng-template
                    ng-label-tmp
                    let-item="item"
                    let-index="index"
                    let-clear="clear"
                >
                    <status-icon
                        [equipment]="item"
                        [clickable]="false"
                    ></status-icon>
                </ng-template>
            </ng-select>
            <span class="input-group-btn">
                <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="
                        working || !addEquipment.valid || isReadOnlyAdmin
                    "
                    (click)="shareEquipment()"
                >
                    Share
                </button>
            </span>
        </div>
        <div>
            <div
                *ngFor="let equipment of relationship.sharedEquipmentList"
                class="top-buffer horizontal-container center-children"
            >
                <equipment-list-item
                    class="shared-item"
                    [item]="equipment"
                    [showCustodian]="true"
                ></equipment-list-item>

                <button
                    *ngIf="!isReadOnlyAdmin"
                    type="button"
                    class="btn btn-tertiary btn-delete"
                    tooltip="Remove sharing of equipment"
                    placement="top"
                    (click)="unshareEquipment(equipment)"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>

                <div>
                    <button
                        *ngIf="equipment.custodian == null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the custodian"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateCustodian(equipment.id)"
                    >
                        Set as Custodian
                    </button>
                    <button
                        *ngIf="equipment.custodian != null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set custodian back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="clearCustodian(equipment.id)"
                    >
                        Clear Custodian
                    </button>
                </div>
            </div>
        </div>`
})
export class SharedEquipment implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;
    @Input() relationship: OrganisationRelationshipDto;
    @Input() isReadOnlyAdmin = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    organisationEquipmentList$: Observable<EquipmentDto[]>;

    addEquipment = new FormControl(undefined, [Validators.required]);

    constructor(
        public workTracker: WorkTracker,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationRelationshipsUi: OrganisationRelationshipsUiService,
        private equipmentService: EquipmentService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.organisationEquipmentList$ =
            this.organisationRelationshipsUi.organisationEquipmentList$.pipe(
                map(res =>
                    res.filter(
                        item => item.organisationId === this.organisation.id
                    )
                )
            );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    shareEquipment() {
        const cmd: ShareResourceCommand = {
            resourceId: this.addEquipment.value
        };
        this.organisationRelationshipService
            .shareEquipment(this.organisation.id, this.relationship.id, cmd)
            .subscribe(results => {
                this.addEquipment.setValue(undefined);
                this.angulartics2.eventTrack.next({
                    action: 'share-equipment',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    unshareEquipment(equipment: EquipmentDto) {
        const cmd: UnshareResourceCommand = { resourceId: equipment.id };
        this.organisationRelationshipService
            .unshareEquipment(this.organisation.id, this.relationship.id, cmd)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'unshare-equipment',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    updateCustodian(equipmentId: number) {
        this.equipmentService
            .updateCustodian(equipmentId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Custodian set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    clearCustodian(equipmentId: number) {
        this.equipmentService
            .updateCustodian(equipmentId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: null
            })
            .subscribe({
                next: () => this.logging.success('Custodian set back to owner'),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
